'use client';

import { FormControl, FormField, FormItem } from '@shared/components/shadcn';
import { getSiteConfig } from '@shared/config';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Checkbox } from '../../../form-controls/Checkbox/Checkbox';
import { FieldInfo } from '../../../form-controls/FieldInfo/FieldInfo';
import { FieldMessage } from '../../../form-controls/FieldMessage/FieldMessage';
import { useMultistepForm } from '../../../form-controls/MultistepForm/hooks/useMultistepForm';
import { PasswordInput } from '../../../form-controls/PasswordInput/PasswordInput';
import { LoginButton } from '../../../LoginButton/index';
import { ContinueButton } from '../components/ContinueButton/ContinueButton';
import { EmailField } from '../components/FormFields/EmailField';
import { StepLayout } from '../components/StepLayout/StepLayout';
import { useStepValidation, useValidationErrorHandler } from '../hooks/usePlayerValidation';
import type { FormValues } from '../types';
import { transformFormDataToApiPayload } from '../utils/payloadTransformer';
import { validateFields } from '../utils/stepValidationUtils';

/**
 * Transforms the nested registration form data into a flat structure expected by the API
 */
function transformRegistrationPayload(formData: FormValues): Record<string, unknown> {
  // Use the shared transformation function as base
  const basePayload = transformFormDataToApiPayload(formData);

  // Override specific fields for CreateAccount step validation
  // Ensure password is included for validation
  return {
    ...basePayload,
    password: formData.createAccount?.password || '',
    gdprChecked: formData.createAccount?.termsAndConditions || false,
    limitAmount: 0,
    limitPeriod: 'DAY',
  };
}

function CreateAccountStep() {
  const t = useTranslations('form');
  const step = useTranslations('register.screens.createAccount');
  const tRegisterValidation = useTranslations('register.validation');
  const form = useFormContext<FormValues>();
  const FORM_ID = 'create-account-form';
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const { updateFormData, goToNextStep, formData, currentStepIndex } = useMultistepForm();
  const { errors } = form.formState;
  const checkboxErrorMessages = errors.createAccount?.termsAndConditions?.message || errors.createAccount?.privacyPolicy?.message;
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const lastValidationStatusCode = useRef<number | undefined>(undefined);
  const lastValidationCaughtError = useRef<unknown>(undefined);
  const tHeader = useTranslations('header.actions_bar');
  const isAustralianMarket = getSiteConfig().REGISTRATION.country === 'AU';
  const { termsCheckboxName, privacyCheckboxName } = getSiteConfig().REGISTRATION.tracking;

  // Restore checkbox values when navigating back to this step
  useEffect(() => {
    // Only restore when this is the active step (step 0)
    if (currentStepIndex === 0) {
      const savedCreateAccountData = formData?.createAccount;
      if (savedCreateAccountData && typeof savedCreateAccountData === 'object') {
        const savedData = savedCreateAccountData as Record<string, unknown>;
        // Restore individual field values to ensure checkboxes update properly
        Object.entries(savedData).forEach(([key, value]) => {
          form.setValue(`createAccount.${key}` as `createAccount.${keyof FormValues['createAccount']}`, value as string | boolean, {
            shouldDirty: false,
            shouldTouch: false,
            shouldValidate: false,
          });
        });
      }
    }
  }, [currentStepIndex, formData, form]);

  // Create a transformation function
  const transformPayload = (formData: FormValues): Record<string, unknown> => {
    return transformRegistrationPayload(formData);
  };

  // Step validation hook
  const { validateWithStatus, isValidating: isLoading } = useStepValidation({
    form,
    fieldMapping: {
      email: 'createAccount.email', // Map API field 'email' to form field 'createAccount.email'
      password: 'createAccount.password', // Map API field 'password' to form field 'createAccount.password'
    },
    transformPayload,
  });

  // Error handler for server errors
  const { handleError } = useValidationErrorHandler();

  // This function can be called by the parent when validating this step
  const validateAndUpdateData = async () => {
    const fieldNames = ['createAccount.email' as const, 'createAccount.password' as const, 'createAccount.termsAndConditions' as const, 'createAccount.privacyPolicy' as const];
    const isFormValid = await validateFields(form, fieldNames);

    if (isFormValid) {
      // Validate player with API using the reusable hook
      // The transformPayload function will handle converting the nested form data to flat API structure
      const {
        success: isApiValid,
        statusCode,
        caughtError,
      } = await validateWithStatus({}, undefined, () => {
        // On successful validation, update the form data
        const createAccountData = form.getValues('createAccount');
        updateFormData('createAccount', createAccountData);
      });

      // Store status code for later use in error handling
      lastValidationStatusCode.current = statusCode;
      lastValidationCaughtError.current = caughtError;

      return isApiValid;
    }

    return false;
  };

  // Handle continue button click
  const handleContinue = async () => {
    const isValid = await validateAndUpdateData();

    if (isValid) {
      // Trigger hidden submit button to prompt browser password save dialog
      submitButtonRef.current?.click();
      goToNextStep();
    } else {
      // Handle server errors using the reusable error handler
      handleError(lastValidationStatusCode.current, lastValidationCaughtError.current);
      // For validation errors (422), just stay on the current step and show form errors
    }
  };

  return (
    <StepLayout title={step('title')} showLogo={false} className={isAustralianMarket ? undefined : 'pt-0'} titleClassName="mb-0" data-testid="create-account-step">
      {/* Create account form fields */}
      <div className="flex flex-col gap-8 align-center">
        <EmailField
          formId={FORM_ID}
          fieldName="createAccount.email"
          autoComplete="username"
          renderErrorMessage={(rawMessage, errorType) =>
            errorType === 'invalid_data'
              ? tRegisterValidation.rich('invalid_data', {
                  loginLink: () => (
                    <LoginButton
                      variant="ghost"
                      size="s"
                      className="inline h-auto rounded-none bg-transparent p-0 link-label hover:bg-transparent md:hover:text-foreground-hover"
                      data-track-name="Log In"
                    >
                      {tHeader('login')}
                    </LoginButton>
                  ),
                })
              : rawMessage
          }
        />

        <div className="flex flex-col">
          <FieldInfo label={t('labels.password')} htmlFor={`${FORM_ID}-password`} />
          <div className="flex flex-row gap-2">
            <FormField
              control={form.control}
              name="createAccount.password"
              render={({ field, fieldState }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <PasswordInput
                      {...field}
                      id={`${FORM_ID}-password`}
                      type="password"
                      autoComplete="new-password"
                      isPasswordVisible={isPasswordVisible}
                      togglePasswordVisible={() => setIsPasswordVisible(!isPasswordVisible)}
                      onFocus={e => {
                        form.trigger('createAccount.password');
                        (field as { onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void }).onFocus?.(e);
                      }}
                      placeholder={t('placeholders.password')}
                      helpMessage={fieldState.error?.message}
                      helpMessageMode="inline-foreground"
                      helpMessageType={fieldState.error ? 'error' : undefined}
                      helperTrackingProps={{
                        'data-track-helper': 'Password',
                      }}
                      inputTrackingProps={{
                        'data-track-name': 'Password',
                        'data-track-privacy': 'Mask',
                      }}
                      data-testid={`${FORM_ID}-password`}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>

        <div className="flex flex-col">
          <div className="flex flex-col gap-5">
            <FormField
              control={form.control}
              name="createAccount.termsAndConditions"
              render={({ field, fieldState }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <Checkbox
                      id={`${FORM_ID}-terms-and-conditions`}
                      checked={field.value}
                      label={t.rich('checkboxes.termsAndConditions', {
                        termsLink: chunks => (
                          <Link
                            href={t('checkboxes.links.termsAndConditions')}
                            target="_blank"
                            className="link-label-explanatory md:hover:text-foreground-hover"
                            data-track-name="Terms Link"
                          >
                            {chunks}
                          </Link>
                        ),
                        privacyLink: chunks => (
                          <Link
                            href={t('checkboxes.links.privacyPolicy')}
                            target="_blank"
                            className="link-label-explanatory md:hover:text-foreground-hover"
                            data-track-name="Privacy Link"
                          >
                            {chunks}
                          </Link>
                        ),
                      })}
                      helpMessageType={fieldState.error ? 'error' : undefined}
                      onCheckedChange={checked => {
                        field.onChange(checked);
                        form.trigger('createAccount.termsAndConditions');
                      }}
                      trackingProps={{
                        'data-track-name': termsCheckboxName,
                        'data-track-privacy': 'Mask',
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="createAccount.privacyPolicy"
              render={({ field, fieldState }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <Checkbox
                      id={`${FORM_ID}-privacy-policy`}
                      checked={field.value}
                      label={t.rich('checkboxes.privacyPolicy', {
                        privacyLink: chunks => (
                          <Link
                            href={t('checkboxes.links.privacyPolicy')}
                            target="_blank"
                            className="link-label-explanatory underline md:hover:text-foreground-hover"
                            data-track-name="Privacy Link"
                          >
                            {chunks}
                          </Link>
                        ),
                      })}
                      helpMessageType={fieldState.error ? 'error' : undefined}
                      onCheckedChange={checked => {
                        field.onChange(checked);
                        form.trigger('createAccount.privacyPolicy');
                      }}
                      trackingProps={{
                        'data-track-name': privacyCheckboxName,
                        'data-track-privacy': 'Mask',
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <FieldMessage
            id={`${FORM_ID}-checkbox-error`}
            message={checkboxErrorMessages}
            messageType={errors.createAccount?.termsAndConditions || errors.createAccount?.privacyPolicy ? 'error' : undefined}
            data-track-helper="Acceptance"
            data-testid={`${FORM_ID}-checkbox-error`}
          />
        </div>

        {/* Hidden submit button for password manager support */}
        <button type="submit" ref={submitButtonRef} className="hidden" aria-hidden="true" tabIndex={-1} />
      </div>

      {/* Continue button and login link */}
      <div className="space-y-3 text-center mt-auto md:mt-0" data-track-container="Actions">
        {/* Continue button */}
        <ContinueButton loading={isLoading} onClick={handleContinue} data-track-name="Continue" />

        {/* Login link */}
        {/* TODO: Go through typography classes in the rebrand, because it looks like they're not consistent */}
        <span className="paragraph">
          <span className="mr-2">{step('alreadyAMember')}</span>
          <LoginButton
            variant="ghost"
            size="s"
            className="inline h-auto rounded-none bg-transparent p-0 link-label underline hover:bg-transparent md:hover:text-foreground-hover"
            data-track-name="Log In"
          >
            {tHeader('login')}
          </LoginButton>
        </span>
      </div>
    </StepLayout>
  );
}

export { CreateAccountStep, transformRegistrationPayload };
